import * as React from "react";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";

import { cn } from "../../utils";

type TabsVariant = "filled" | "minimal";

interface TabsContextValue {
  variant: TabsVariant;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = React.useContext(TabsContext);

  if (!context) {
    throw new Error("useTabsContext must be used within a TabsContextProvider");
  }

  return context;
};

interface TabsProps {
  variant?: TabsVariant;
}

const Tabs = ({
  variant = "filled",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & TabsProps) => (
  <TabsContext
    value={{
      variant,
    }}
  >
    <TabsPrimitive.Root {...props} />
  </TabsContext>
);
Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsListVariants = cva(
  "af-flex af-w-min af-justify-center af-rounded-lg af-text-muted-foreground data-[orientation=vertical]:af-flex-col data-[orientation=vertical]:af-items-start data-[orientation=horizontal]:af-items-center data-[orientation=vertical]:af-justify-start",
  {
    variants: {
      variant: {
        filled: "af-bg-muted af-p-1 data-[orientation=horizontal]:af-h-9",
        minimal: "",
      },
    },
  },
);

const TabsList = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) => {
  const { variant } = useTabsContext();

  return (
    <TabsPrimitive.List
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
};
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  "af-inline-flex af-items-center af-justify-center af-whitespace-nowrap af-text-sm af-font-medium af-ring-offset-background af-transition-all focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2 disabled:af-pointer-events-none disabled:af-opacity-50",
  {
    variants: {
      variant: {
        filled:
          "af-rounded-md data-[state=active]:af-bg-background data-[orientation=horizontal]:af-px-3 data-[orientation=horizontal]:af-py-1 data-[orientation=vertical]:af-px-1 data-[orientation=vertical]:af-py-3 data-[state=active]:af-shadow",
        minimal:
          "af-gap-1 af-text-sm af-font-semibold data-[state=active]:af-text-foreground data-[state=inactive]:af-text-[#396BEE] data-[state=active]:af-underline [&>svg]:data-[state=active]:af-text-[#6F6E77]",
      },
    },
  },
);

const TabsTrigger = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) => {
  const { variant } = useTabsContext();

  return (
    <TabsPrimitive.Trigger
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
};
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    className={cn(
      "af-bg-background af-text-foreground af-ring-offset-background focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2",
      "data-[orientation=horizontal]:af-mt-2",
      className,
    )}
    {...props}
  />
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
